import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FileText, Inbox, CreditCard, BarChart3, Store, Settings,
  Boxes, ChevronDown, Lock, ShieldCheck, Wallet,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem,
} from "./ui/dropdown-menu";
import { LogOut, Briefcase, Check } from "lucide-react";

/**
 * Desktop-only olive-green sidebar for VOW Control Center.
 * Hidden on mobile (`hidden lg:flex`) — mobile users continue to use the bottom nav from Layout.jsx.
 *
 * Visibility is BOTH vertical-aware (Phase 6 Part 1) and permission-aware
 * (Phase 1). Server enforces both — the sidebar just mirrors the resolved
 * state so a user never sees a nav item they couldn't reach anyway.
 */
const NAV = [
  // Finance vertical
  { to: "/dashboard",   icon: LayoutDashboard, label: "Dashboard",   perm: null,                vertical: "finance" },
  { to: "/my-requests", icon: FileText,        label: "Requests",    perm: "create_requests",   vertical: "finance" },
  { to: "/approvals",   icon: Inbox,           label: "Approvals",   perm: "approve_requests",  vertical: "finance" },
  { to: "/queue",       icon: CreditCard,      label: "Payments",    perm: "view_payment_queue", vertical: "finance" },
  { to: "/reports",     icon: BarChart3,       label: "Reports",     perm: "view_reports",      vertical: "finance" },
  { to: "/admin",       icon: Store,           label: "Vendors",     perm: "manage_vendors",    vertical: "finance", hash: "#vendors" },
  // Phase F3 — Confidential vault (invisible unless view_confidential is granted).
  { to: "/confidential", icon: Lock,           label: "Confidential", perm: "view_confidential", vertical: "finance" },
  // Phase C1 — Cash Flow (invisible unless manage_cashflow is granted).
  { to: "/cashflow",    icon: Wallet,          label: "Cash Flow",   perm: "manage_cashflow",   vertical: "finance" },
  // Inventory vertical
  { to: "/inventory",   icon: Boxes,           label: "Inventory",   perm: null,                vertical: "inventory" },
  // Licenses vertical (Phase L1) — visible only to view_licenses holders.
  { to: "/licenses",    icon: ShieldCheck,     label: "Licenses",    perm: "view_licenses",     vertical: "licenses" },
  // Admin (cross-vertical — admin always has both verticals so always visible to admins)
  { to: "/admin",       icon: Settings,        label: "Settings",    perm: "manage_users",      vertical: null },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  if (!user) return null;

  const verticals = user.verticals || ["finance"];
  const isAdmin = user.role === "admin";
  const activeVerticals = new Set(verticals);
  const shownVerticalCount = ["finance", "inventory", "licenses"].filter((v) => activeVerticals.has(v) || isAdmin).length;
  const showGroupHeaders = shownVerticalCount >= 2;

  const items = NAV.filter((n) => {
    // Vertical gate first — universal items (vertical=null) always pass.
    if (n.vertical && !verticals.includes(n.vertical) && !isAdmin) return false;
    // Permission gate
    if (!n.perm) return true;
    // Phase F3 carve-out — view_confidential is opt-in even for admins,
    // EXCEPT the Master Admin (system owner) who always has it.
    if (n.perm === "view_confidential") {
      if (user.is_master_admin === true) return true;
      return !!(user.permissions_effective || {}).view_confidential;
    }
    // Phase C1 — manage_cashflow is sealed just like view_confidential.
    // Master Admin always in; other admins must be explicitly granted.
    if (n.perm === "manage_cashflow") {
      if (user.is_master_admin === true) return true;
      return !!(user.permissions_effective || {}).manage_cashflow;
    }
    if (isAdmin) return true;
    return !!(user.permissions_effective || {})[n.perm];
  });

  // Group items by vertical so the switcher can section them
  const financeItems = items.filter((n) => n.vertical === "finance");
  const inventoryItems = items.filter((n) => n.vertical === "inventory");
  const licensesItems = items.filter((n) => n.vertical === "licenses");
  const universalItems = items.filter((n) => !n.vertical);
  const activePath = loc.pathname;

  return (
    <aside
      data-testid="sidebar"
      className="hidden lg:flex flex-col w-60 xl:w-64 shrink-0 bg-white border-r border-slate-200 sticky top-0 h-screen text-slate-900"
    >
      {/* Brand */}
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-baseline gap-2">
          <span className="font-display font-black text-3xl tracking-tighter">VOW</span>
          <span className="text-[11px] font-medium tracking-wide text-olive">Control</span>
        </div>
        {/* Workspace switching moved to the user-menu dropdown (top-right avatar) — single source of truth */}
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-3 space-y-1 pb-3 overflow-y-auto">
        {financeItems.length > 0 && (
          <SidebarGroup
            label={showGroupHeaders ? "Finance" : null}
            items={financeItems}
            activePath={activePath}
            navigate={navigate}
          />
        )}
        {inventoryItems.length > 0 && (
          <SidebarGroup
            label={showGroupHeaders ? "Inventory" : null}
            items={inventoryItems}
            activePath={activePath}
            navigate={navigate}
          />
        )}
        {licensesItems.length > 0 && (
          <SidebarGroup
            label={showGroupHeaders ? "Licenses" : null}
            items={licensesItems}
            activePath={activePath}
            navigate={navigate}
          />
        )}
        {universalItems.length > 0 && (
          <SidebarGroup
            label={showGroupHeaders ? "Admin" : null}
            items={universalItems}
            activePath={activePath}
            navigate={navigate}
          />
        )}
      </nav>

      {/* Footer — user + logout */}
      <div className="px-3 pb-6 pt-4 mt-2 border-t border-white/10">
        <SidebarUserMenu />
      </div>
    </aside>
  );
}

/**
 * Sidebar footer — identity row that opens the same workspace switcher as the
 * header avatar dropdown (single source of truth for workspace switching).
 */
function SidebarUserMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  if (!user) return null;
  const isAdmin = user.role === "admin";
  const verticals = user.verticals || ["finance"];
  const hasFinance = isAdmin || verticals.includes("finance");
  const hasInventory = isAdmin || verticals.includes("inventory");
  const hasBoth = hasFinance && hasInventory;
  const active = location.pathname.startsWith("/inventory") ? "inventory" : "finance";
  const goTo = (v) => navigate(v === "inventory" ? "/inventory" : "/dashboard");
  const initial = (user.name || user.email || "?").charAt(0).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="sidebar-user-trigger"
          className="w-full px-4 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 flex items-center gap-3 transition-colors text-left"
        >
          <div className="w-9 h-9 rounded-full bg-olive text-white grid place-items-center font-display font-black text-sm shrink-0">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-sm font-bold truncate">{user.name || user.email}</div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{user.role}</div>
          </div>
          <ChevronDown size={14} strokeWidth={2} className="text-slate-400 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        sideOffset={6}
        className="w-72 rounded-2xl border-slate-200 p-1.5"
        data-testid="sidebar-user-menu-content"
      >
        <div className="px-3 py-2.5">
          <div className="text-sm font-semibold text-slate-900 truncate">{user.name || user.email}</div>
          {user.name && <div className="text-[11px] text-slate-500 truncate">{user.email}</div>}
          <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold uppercase tracking-wider">{user.role}</span>
        </div>

        {hasBoth && (
          <>
            <DropdownMenuSeparator className="my-1" />
            <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 px-3 pt-2 pb-1">
              Workspace
            </DropdownMenuLabel>
            <div data-testid="sidebar-workspace-switcher" className="space-y-1 px-1">
              <WorkspaceRow
                testid="sidebar-workspace-row-finance"
                label="Finance"
                desc="Requests · approvals · payments · reports"
                Icon={Briefcase}
                active={active === "finance"}
                onClick={() => goTo("finance")}
              />
              <WorkspaceRow
                testid="sidebar-workspace-row-inventory"
                label="Inventory"
                desc="Stock · transfers · breakage · valuation"
                Icon={Boxes}
                active={active === "inventory"}
                onClick={() => goTo("inventory")}
              />
            </div>
          </>
        )}

        <DropdownMenuSeparator className="my-1" />
        <DropdownMenuItem
          data-testid="sidebar-user-logout"
          onClick={logout}
          className="rounded-xl cursor-pointer text-slate-700 focus:bg-slate-100 focus:text-slate-900 px-3 py-2 text-sm font-medium gap-2"
        >
          <LogOut size={14} strokeWidth={1.9} /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const WorkspaceRow = ({ testid, label, desc, Icon, active, onClick }) => (
  <button
    data-testid={testid}
    data-active={active ? "true" : "false"}
    onClick={onClick}
    className={
      "w-full text-left rounded-xl border px-3 py-2.5 flex items-center gap-2.5 transition-colors " +
      (active ? "bg-olive/5 border-olive/40" : "bg-white border-slate-100 hover:bg-slate-50 hover:border-slate-200")
    }
  >
    <div className={"w-9 h-9 rounded-xl grid place-items-center shrink-0 " + (active ? "bg-olive/15 text-olive" : "bg-slate-100 text-slate-500")}>
      <Icon size={15} strokeWidth={1.8} />
    </div>
    <div className="flex-1 min-w-0">
      <div className={"text-sm font-semibold " + (active ? "text-olive" : "text-slate-900")}>
        {label}
        {active && (
          <span data-testid={`${testid}-current-marker`} className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider text-olive/80">· Current</span>
        )}
      </div>
      <div className="text-[11px] text-slate-500 truncate mt-0.5">{desc}</div>
    </div>
    {active && <Check size={14} strokeWidth={2.2} className="text-olive shrink-0" />}
  </button>
);

const SidebarGroup = ({ label, items, activePath, navigate }) => (
  <div className="space-y-1">
    {label && (
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 px-4 pt-2 pb-1">
        {label}
      </div>
    )}
    {items.map((n) => {
      const Icon = n.icon;
      const active = activePath === n.to;
      return (
        <button
          key={`${n.to}-${n.label}`}
          data-testid={`sidebar-nav-${n.label.toLowerCase()}`}
          onClick={() => navigate(n.to)}
          className={`w-full text-left flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${
            active
              ? "bg-olive/10 text-olive font-semibold"
              : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
          }`}
        >
          <Icon size={18} strokeWidth={1.8} />
          <span>{n.label}</span>
        </button>
      );
    })}
  </div>
);
